<template>
  <div class="flex flex-col gap-3">
    <!-- Родительский фильтр -->
    <div class="flex flex-col gap-1">
      <label class="font-semibold" :for="name">{{ label }}</label>
      <Multiselect
        ref="dropdownRef"
        v-model="parentValue"
        :options="parentOptions"
        :multiple="false"
        :object="true"
        @select="handleParentSelect"
        :canClear="true"
        @clear="clearChildren"
        :trackBy="'value'"
        :label="'label'"
        :valueProp="'value'"
      />
    </div>

    <!-- Дочерние значения -->
    <div v-if="parentValue" class="flex flex-col gap-1">
      <label class="font-semibold" :for="'child'">{{ childLabel }}</label>
      <Multiselect
        v-model="childValues"
        :options="childrenOptions"
        mode="tags"
        @change="handleChildrenChange"
        :canClear="true"
        @clear="clearChildren"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Multiselect from '@vueform/multiselect';
import api from '@/api';
import { useFilterStore } from '@/store/filterStore.js';

const props = defineProps({
  label: { type: String, required: true },
  name: { type: String, required: true },
  property: { type: Number, required: true },
  dataType: { type: Number, required: true },
});

const filterStore = useFilterStore();

const dropdownRef = ref(null);
const parentValue = ref(null);
const childValues = ref([]);
const parentOptions = ref([]);
const childrenOptions = ref([]);
const childLabel = ref('');

const page = ref(1);
const hasNext = ref(false);
const isLoading = ref(false);

const fetchOptions = async (pageNum = 1) => {
  isLoading.value = true;
  try {
    const res = await api.get(
      `attributes/select_values/?property=${props.property}&include_children=true&page=${pageNum}`,
    );

    const data = res.data;
    const results = Array.isArray(data) ? data : Array.isArray(data.results) ? data.results : [];
    const next = data.next || null;

    hasNext.value = !!next;

    const mapped = results.map(item => ({
      value: item.id,
      label: item.name,
      children: item.children,
    }));

    parentOptions.value = [...parentOptions.value, ...mapped];
  } catch (err) {
    console.error('Ошибка загрузки опций:', err);
  } finally {
    isLoading.value = false;
  }
};

const clearChildren = () => {
  childValues.value = [];
  childrenOptions.value = [];
  childLabel.value = '';
  parentValue.value = null;

  filterStore.removeFilterByProperty(props.property); // ⬅️ ВАЖНО!
  filterStore.fetchDynamicCount();
};

const handleParentSelect = option => {
  if (!option) return clearChildren();

  parentValue.value = option;

  childrenOptions.value = Array.isArray(option.children)
    ? option.children.map(child => ({
        value: child?.id,
        label: child?.name?.trim() || `ID ${child?.id}`,
      }))
    : [];

  console.log('▶️ Children options built:', childrenOptions.value);

  childLabel.value = option.label;

  filterStore.updateFilter({
    type: 'nested_parent',
    property: props.property,
    value: option,
  });

  filterStore.fetchDynamicCount();
};

const handleChildrenChange = () => {
  filterStore.updateFilter({
    type: 'nested_children',
    property: props.property,
    value: childValues.value,
  });

  filterStore.fetchDynamicCount();
};

const onScroll = () => {
  const list = dropdownRef.value?.$el?.querySelector('.multiselect-dropdown');
  if (
    list &&
    list.scrollTop + list.clientHeight >= list.scrollHeight - 10 &&
    !isLoading.value &&
    hasNext.value
  ) {
    page.value += 1;
    fetchOptions(page.value);
  }
};

onMounted(() => {
  fetchOptions();
  const list = dropdownRef.value?.$el?.querySelector('.multiselect-dropdown');
  if (list) {
    list.addEventListener('scroll', onScroll);
  }
});

onBeforeUnmount(() => {
  const list = dropdownRef.value?.$el?.querySelector('.multiselect-dropdown');
  if (list) {
    list.removeEventListener('scroll', onScroll);
  }
});
</script>

<style scoped>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
